/**
 * VIB-2980: Idé-tavlen i tilbudsarbeidsrommet (docs/tilbudsarbeidsrom/00-kjoreplan.md §6).
 *
 * Ren logikk for tavlen: korttyper, plassering, grupper, utdrag fra
 * kundekortet og koblingen kort → budsjettlinje. Kortene er interne og
 * vises aldri for kunden.
 */
import type { DBOfferIdeaCard, DBOfferIdeaGroup, OfferIdeaCardKind } from "@/lib/db-types";

export const IDEA_KIND_LABEL: Record<OfferIdeaCardKind, string> = {
  note: "Notat",
  image: "Bilde",
  link: "Lenke",
  film: "Referansefilm",
  question: "Åpent spørsmål",
  excerpt: "Utdrag",
};

/** Ikon per korttype (SrIcon/Lucide-navn). */
export const IDEA_KIND_ICON: Record<OfferIdeaCardKind, string> = {
  note: "sticky-note",
  image: "image",
  link: "link",
  film: "film",
  question: "circle-help",
  excerpt: "quote",
};

/** dataTransfer-typen når en aktivitet dras fra kundekortpanelet til tavlen. */
export const EXCERPT_DRAG_TYPE = "application/x-revehiet-idea-excerpt";

/** Kort som lages fra verktøylinjen. Utdrag lages fra kundekortet. */
export const CREATABLE_KINDS: readonly OfferIdeaCardKind[] = ["note", "image", "link", "film", "question"];

export const CARD_WIDTH = 240;
export const BOARD_WIDTH = 2400;
export const BOARD_HEIGHT = 1600;
const GRID_X = CARD_WIDTH + 24;
const GRID_Y = 200;
const MARGIN = 24;

type Positioned = Pick<DBOfferIdeaCard, "x" | "y">;

/** Holder et punkt innenfor tavlen. */
export function clampToBoard(x: number, y: number, width = CARD_WIDTH): { x: number; y: number } {
  return {
    x: Math.round(Math.min(Math.max(0, x), BOARD_WIDTH - width)),
    y: Math.round(Math.min(Math.max(0, y), BOARD_HEIGHT - 80)),
  };
}

/**
 * Første ledige plass i et rutenett øverst til venstre, så et nytt kort ikke
 * havner oppå et annet. Et felt er opptatt når et kort starter mindre enn en
 * halv rute unna.
 */
export function nextCardPosition(cards: readonly Positioned[]): { x: number; y: number } {
  const columns = Math.floor((BOARD_WIDTH - MARGIN) / GRID_X);
  for (let i = 0; i < columns * 20; i++) {
    const x = MARGIN + (i % columns) * GRID_X;
    const y = MARGIN + Math.floor(i / columns) * GRID_Y;
    const taken = cards.some((c) => Math.abs(c.x - x) < GRID_X / 2 && Math.abs(c.y - y) < GRID_Y / 2);
    if (!taken) return { x, y };
  }
  return { x: MARGIN, y: MARGIN };
}

type GroupRect = Pick<DBOfferIdeaGroup, "id" | "x" | "y" | "width" | "height">;

/** Gruppen et punkt ligger i. Ved overlapp vinner den siste (øverst tegnet). */
export function groupAtPoint(groups: readonly GroupRect[], x: number, y: number): string | null {
  let hit: string | null = null;
  for (const g of groups) {
    if (x >= g.x && x <= g.x + g.width && y >= g.y && y <= g.y + g.height) hit = g.id;
  }
  return hit;
}

/** Nye posisjoner for kortene i en gruppe når gruppen flyttes. */
export function moveGroupMembers(
  cards: readonly Pick<DBOfferIdeaCard, "id" | "x" | "y" | "group_id">[],
  groupId: string,
  dx: number,
  dy: number,
): { id: string; x: number; y: number }[] {
  return cards
    .filter((c) => c.group_id === groupId)
    .map((c) => ({ id: c.id, ...clampToBoard(c.x + dx, c.y + dy) }));
}

/**
 * Vimeo-id fra en id eller en lenke (vimeo.com/123, vimeo.com/kanal/123,
 * player.vimeo.com/video/123). null når det ikke er en Vimeo-film.
 */
export function parseVimeoId(input: string): string | null {
  const s = input.trim();
  if (/^\d{5,}$/.test(s)) return s;
  const m = s.match(/^(?:https?:\/\/)?(?:www\.|player\.)?vimeo\.com\/(?:[^?#]*\/)?(\d{5,})(?:[/?#]|$)/i);
  return m ? m[1] : null;
}

/** Legger på https:// når lenken mangler protokoll. Tom tekst gir null. */
export function normalizeUrl(input: string): string | null {
  const s = input.trim();
  if (!s) return null;
  return /^https?:\/\//i.test(s) ? s : `https://${s}`;
}

/** Bildetypene bøtta offer-media tar imot, med filendelse. */
export const IDEA_IMAGE_TYPES: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
  "image/gif": "gif",
};

/** Sti i bøtta offer-media for et bilde på et Idé-kort. null for andre filtyper. */
export function ideaImagePath(offerId: string, mime: string, id: string): string | null {
  const ext = IDEA_IMAGE_TYPES[mime];
  return ext ? `offers/${offerId}/idea/${id}.${ext}` : null;
}

export interface ExcerptSource {
  id: string;
  type: string;
  typeLabel: string;
  subject: string | null;
  body: string | null;
  occurred_at: string;
  userName: string | null;
}

export interface ExcerptFields {
  kind: "excerpt";
  body: string;
  source_type: "customer_activity";
  source_id: string;
  source_label: string;
  source_date: string;
}

/**
 * Utdrag fra en aktivitet på kundekortet. Teksten og kilden kopieres inn,
 * så kortet står seg om aktiviteten senere endres eller faller ut av listen.
 */
export function excerptFromActivity(a: ExcerptSource): ExcerptFields {
  const body = [a.subject, a.body].filter((s) => s && s.trim()).join("\n\n").trim();
  return {
    kind: "excerpt",
    body,
    source_type: "customer_activity",
    source_id: a.id,
    source_label: a.userName ? `${a.typeLabel} · ${a.userName}` : a.typeLabel,
    source_date: a.occurred_at,
  };
}

/**
 * Leser et utdrag som er dratt inn på tavlen. Dra-data kan komme fra andre
 * vinduer, så bare de kjente feltene tas med, og alt annet gir null.
 */
export function parseDroppedExcerpt(raw: string): ExcerptFields | null {
  let v: unknown;
  try {
    v = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!v || typeof v !== "object") return null;
  const o = v as Record<string, unknown>;
  const str = (x: unknown) => (typeof x === "string" ? x : null);
  const body = str(o.body);
  const sourceId = str(o.source_id);
  const label = str(o.source_label);
  const date = str(o.source_date);
  if (body === null || !sourceId || !label || !date) return null;
  return {
    kind: "excerpt",
    body: body.slice(0, 5000),
    source_type: "customer_activity",
    source_id: sourceId,
    source_label: label.slice(0, 200),
    source_date: date,
  };
}

/** Id-ene til kort som er brukt i budsjettet. */
export function cardsUsedInBudget(
  lines: readonly { source_idea_card_id?: string | null }[],
): Set<string> {
  const used = new Set<string>();
  for (const l of lines) if (l.source_idea_card_id) used.add(l.source_idea_card_id);
  return used;
}

const MAX_COMMENT = 500;

/**
 * Kommentaren på budsjettlinjen et kort blir til. Beskrivelsen er
 * katalogproduktets navn, så timeføring og konvertering matcher som før.
 */
export function ideaLineComment(card: Pick<DBOfferIdeaCard, "kind" | "body" | "url">): string | null {
  const text = [card.body.trim(), card.kind === "link" ? card.url ?? "" : ""].filter(Boolean).join("\n");
  if (!text) return null;
  return text.length > MAX_COMMENT ? `${text.slice(0, MAX_COMMENT - 1)}…` : text;
}
